import {EXTEND, IS, IS_NOT, nil, NO, UIRoute, UIView, UIViewController, YES} from "uicore-ts"
import {RHContractingAuthorityRules} from "../ContractingAuthorityRules"
import {RHCore} from "../Core/RHCore"
import {RHColor} from "../Custom components/RH/RHColor"
import type {RHBreadcrumbItem} from "../Custom components/RH/RHBreadcrumbView"
import {RHTextSanitizer} from "../Custom components/RH/Utils_slop/RHTextSanitizer"
import {RHSegmentedTabBarView} from "../Custom components/RH/RHSegmentedTabBarView"
import {RHButtons, RHSpacing} from "../Custom components/RH/Utils_slop/RHDesignTokens"
import {
    isRHNavigationResettable,
    RHNavigationResettable
} from "../Custom components/RH/Utils_slop/RHNavigationResettable"
import {
    ContractingAuthorityNotConfiguredViewController
} from "../NewProcurement/ContractingAuthorityNotConfiguredViewController"
import {NewProcurementViewController} from "../NewProcurement/NewProcurementViewController"
import {MyOngoingProcurementsViewController} from "../OngoingProcurements/MyOngoingProcurementsViewController"
import {DraftProcurementsViewController} from "./DraftProcurementsViewController"
import {UserMainViewController} from "./UserMainViewController"


type ProcurerMainTab = "published" | "drafts" | "new"

const ProcurerMainTabKey: Record<ProcurerMainTab, ProcurerMainTab> = {
    published: "published",
    drafts: "drafts",
    new: "new"
}

const PROCURER_MAIN_TAB_KEYS = Object.values(ProcurerMainTabKey) as ProcurerMainTab[]


/**
 * ProcurerMainViewController — the procurer "Töölaud" shown inside
 * UserMainViewController's role-aware `user_main` route. It owns the
 * Minu hanked / Ettevalmistamisel ja mallid / Uus tab row and swaps only the child content.
 */
export class ProcurerMainViewController extends UIViewController implements RHNavigationResettable {
    
    static override readonly routeComponentName = "procurer_main"
    static override readonly ParameterIdentifierName = {
        tab: "tab"
    }
    
    readonly tabBar = new RHSegmentedTabBarView([
        { key: ProcurerMainTabKey.published, title: "Minu hanked" },
        { key: ProcurerMainTabKey.drafts, title: "Ettevalmistamisel ja mallid" },
        {
            key: ProcurerMainTabKey.new,
            title: "Alusta uut hanget",
            unselectedVariant: "success"
        }
    ]).configuredWithObject({
        selectionDidChange: (key) => {
            if (key !== ProcurerMainTabKey.new) {
                this._showTab(key as ProcurerMainTab).then(nil)
            }
        }
    }).addedAsSubviewToView(this.view)
    
    readonly contentPanelView = new UIView().addedAsSubviewToView(this.view).configuredWithObject({
        layoutSubviews: EXTEND(() => {
            if (IS(this.contentPanelView.subviews.firstElement)) {
                this.contentPanelView.subviews.firstElement.frame =
                    this.contentPanelView.bounds.rectangleWithHeight(
                        this.contentPanelView.subviews.firstElement
                    )
            }
        })
    })
    
    private _ongoingProcurementsViewController: MyOngoingProcurementsViewController | undefined
    private _draftProcurementsViewController: DraftProcurementsViewController | undefined
    private _newProcurementViewController: NewProcurementViewController | undefined
    _contractingAuthorityNotConfiguredViewController: ContractingAuthorityNotConfiguredViewController | undefined
    
    // Last tab the procurer was on. Used when returning to `user_main` without
    // an explicit tab parameter, for example from procurement details.
    _lastTab: ProcurerMainTab = ProcurerMainTabKey.published


    get breadcrumbItems(): RHBreadcrumbItem[] {
        const child = this._mountedChildViewController()
        if (child instanceof NewProcurementViewController) {
            return child.breadcrumbItems
        }
        const dashboardRoute = UIRoute.currentRoute.routeByRemovingComponentsOtherThanOnesNamed(["settings"])
            .routeWithViewControllerComponent(UserMainViewController, { tab: ProcurerMainTabKey.published })
        const tabButton = this.tabBar.tabButtonForKey(this._currentTab)
        const items: RHBreadcrumbItem[] = [{ title: "Töölaud", route: dashboardRoute }]
        if (tabButton) {
            items.push({ title: RHTextSanitizer.plainText(tabButton.titleLabel.text) })
        }
        return items
    }
    
    
    private get ongoingProcurementsViewController(): MyOngoingProcurementsViewController {
        if (!this._ongoingProcurementsViewController) {
            this._ongoingProcurementsViewController = new MyOngoingProcurementsViewController(
                new UIView("ProcurerMainOngoingProcurementsView")
            )
            this._ongoingProcurementsViewController.backToRouteComponentName =
                UserMainViewController.routeComponentName
            this._ongoingProcurementsViewController.showsPageTitle = NO
        }
        return this._ongoingProcurementsViewController
    }
    
    private get draftProcurementsViewController(): DraftProcurementsViewController {
        if (!this._draftProcurementsViewController) {
            this._draftProcurementsViewController = new DraftProcurementsViewController(
                new UIView("ProcurerMainDraftProcurementsView")
            )
        }
        return this._draftProcurementsViewController
    }
    
    private get newProcurementViewController(): NewProcurementViewController {
        if (!this._newProcurementViewController) {
            this._newProcurementViewController = new NewProcurementViewController(
                new UIView("ProcurerMainNewProcurementView")
            )
            this._newProcurementViewController.hostRouteComponentName =
                UserMainViewController.routeComponentName
            this._newProcurementViewController.backToRouteComponentName =
                UserMainViewController.routeComponentName
        }
        return this._newProcurementViewController
    }


    get contractingAuthorityNotConfiguredViewController(): ContractingAuthorityNotConfiguredViewController {
        if (!this._contractingAuthorityNotConfiguredViewController) {
            this._contractingAuthorityNotConfiguredViewController = new ContractingAuthorityNotConfiguredViewController(
                new UIView("ProcurerMainContractingAuthorityNotConfiguredView")
            )
        }
        return this._contractingAuthorityNotConfiguredViewController
    }


    // A procurement can't be created until the active company has at least one
    // contracting-authority type configured, so the "new" tab never constructs
    // NewProcurementViewController until this is true - see
    // _mountNewProcurementTabContent.
    get _hasConfiguredContractingAuthorityType(): boolean {
        const company = RHCore.sharedInstance.companyAffiliationStore.activeCompanyAffiliation?.company ?? {}
        return RHContractingAuthorityRules.allowedTypesForCompany(company).length > 0
    }


    override viewDidReceiveBroadcastEvent(event: { name: string }) {
        super.viewDidReceiveBroadcastEvent(event)
        if (event.name === RHCore.broadcastEventName.activeCompanyDidChange && this._currentTab === ProcurerMainTabKey.new) {
            this._mountNewProcurementTabContent().then(nil)
        }
    }
    
    
    constructor(view: UIView) {
        super(view)
        this.view.configureWithObject({
            backgroundColor: RHColor.background
        })
        this.tabBar.tabButtonForKey(ProcurerMainTabKey.new)!.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this._showTab(ProcurerMainTabKey.new).then(nil)
        }
    }
    
    
    // ── Routing / tab state ─────────────────────────────────────────────────
    
    private get _currentTab(): ProcurerMainTab {
        return this._tabForRoute(UIRoute.currentRoute)
    }
    
    
    _tabForRoute(route: UIRoute): ProcurerMainTab {
        const tabParameter = this._tabParameterInRoute(route)
        
        if ((PROCURER_MAIN_TAB_KEYS as readonly string[]).includes(tabParameter ?? "")) {
            return tabParameter as ProcurerMainTab
        }
        return this._lastTab
    }
    
    
    _tabParameterInRoute(route: UIRoute): string | undefined {
        return route
            .componentWithViewController(UserMainViewController)
            ?.parameters[UserMainViewController.ParameterIdentifierName.tab] as string | undefined
    }
    
    
    _routeNeedsTabParameterForTab(route: UIRoute, tab: ProcurerMainTab): boolean {
        if (!this._routeMayUseProcurerTabParameter(route)) {
            return NO
        }
        
        return this._tabParameterInRoute(route) !== tab
    }
    
    
    _routeMayUseProcurerTabParameter(route: UIRoute): boolean {
        if (IS_NOT(route.componentWithViewController(UserMainViewController))) {
            return NO
        }
        
        const modeParameter = route.componentWithName("settings")?.parameters["mode"]
        if (modeParameter === "offerer" || modeParameter === "auditor") {
            return NO
        }
        return YES
    }
    
    
    private async _showTab(tab: ProcurerMainTab) {
        if (IS_NOT(UIRoute.currentRoute.componentWithViewController(UserMainViewController))) {
            return
        }
        
        const currentTab = this._currentTab
        if (tab === currentTab && tab !== ProcurerMainTabKey.new) {
            return
        }
        const canLeaveCurrentTab = await this._canLeaveCurrentTab()
        if (!canLeaveCurrentTab) {
            this.tabBar.selectedKey = currentTab
            return
        }
        let targetRoute = UIRoute.currentRoute
        if (tab === ProcurerMainTabKey.new) {
            if (
                this._hasConfiguredContractingAuthorityType &&
                !await this.newProcurementViewController.prepareForNewProcurement()
            ) {
                return
            }
            targetRoute = UIRoute.currentRoute.routeByRemovingComponentNamed(
                NewProcurementViewController.routeComponentName
            )
        }
        targetRoute
            .routeBySettingParameterInComponent(
                UserMainViewController.routeComponentName,
                UserMainViewController.ParameterIdentifierName.tab,
                tab
            )
            .apply()
    }
    
    
    async navigationWillReset(): Promise<boolean> {
        return await this._canLeaveCurrentTab()
    }
    
    
    private async _canLeaveCurrentTab(): Promise<boolean> {
        const childViewController = this._mountedChildViewController()
        if (isRHNavigationResettable(childViewController)) {
            return await childViewController.navigationWillReset()
        }
        return YES
    }
    
    
    _mountedChildViewController(): UIViewController | undefined {
        return [
            this._ongoingProcurementsViewController,
            this._draftProcurementsViewController,
            this._newProcurementViewController,
            this._contractingAuthorityNotConfiguredViewController
        ].find(viewController => IS(viewController) && this.hasChildViewController(viewController))
    }
    
    
    override async handleRoute(route: UIRoute) {
        if (IS_NOT(route.componentWithViewController(UserMainViewController))) {
            return
        }
        
        const tab = this._tabForRoute(route)
        if (this._routeNeedsTabParameterForTab(route, tab)) {
            const routeWasApplied = await route
                .routeBySettingParameterInComponent(
                    UserMainViewController.routeComponentName,
                    UserMainViewController.ParameterIdentifierName.tab,
                    tab
                )
                .applyByReplacingCurrentRouteInHistory()
            if (routeWasApplied) {
                return
            }
        }
        
        this._lastTab = tab
        await this._mountChildViewControllerForTab(tab)
        this.tabBar.selectedKey = tab
        await super.handleRoute(route)
    }
    
    
    // ── Child VC management ─────────────────────────────────────────────────
    
    private async _mountChildViewControllerForTab(tab: ProcurerMainTab) {
        if (tab === ProcurerMainTabKey.published) {
            this._removeChildIfMounted(this._draftProcurementsViewController)
            this._removeChildIfMounted(this._newProcurementViewController)
            this._removeChildIfMounted(this._contractingAuthorityNotConfiguredViewController)
            await this._mountChildIfNeeded(this.ongoingProcurementsViewController)
        }
        else if (tab === ProcurerMainTabKey.drafts) {
            this._removeChildIfMounted(this._ongoingProcurementsViewController)
            this._removeChildIfMounted(this._newProcurementViewController)
            this._removeChildIfMounted(this._contractingAuthorityNotConfiguredViewController)
            await this._mountChildIfNeeded(this.draftProcurementsViewController)
        }
        else {
            this._removeChildIfMounted(this._ongoingProcurementsViewController)
            this._removeChildIfMounted(this._draftProcurementsViewController)
            await this._mountNewProcurementTabContent()
        }

        this.view.setNeedsLayoutUpToRootView()
    }


    // A procurement can't be created until the active company has a configured
    // contracting-authority type, so this picks which of the two controllers
    // occupies the "new" tab rather than always mounting NewProcurementViewController.
    // Also re-run directly on an active-company broadcast (see
    // viewDidReceiveBroadcastEvent) so switching company while already on this tab
    // swaps the mounted controller instead of leaving the wrong one showing.
    async _mountNewProcurementTabContent() {
        if (this._hasConfiguredContractingAuthorityType) {
            this._removeChildIfMounted(this._contractingAuthorityNotConfiguredViewController)
            await this._mountChildIfNeeded(this.newProcurementViewController)
        }
        else {
            this._removeChildIfMounted(this._newProcurementViewController)
            await this._mountChildIfNeeded(this.contractingAuthorityNotConfiguredViewController)
        }
    }
    
    
    private async _mountChildIfNeeded(viewController: UIViewController) {
        if (!this.hasChildViewController(viewController)) {
            this.addChildViewControllerInContainer(viewController, this.contentPanelView)
            await viewController.viewWillAppear()
            viewController.viewDidAppear().then(nil)
        }
    }
    
    
    private _removeChildIfMounted(viewController: UIViewController | undefined) {
        if (IS(viewController) && this.hasChildViewController(viewController)) {
            viewController.viewWillDisappear().then(nil)
            this.removeChildViewController(viewController)
        }
    }
    
    
    // ── Layout ──────────────────────────────────────────────────────────────
    
    override layoutViewSubviews() {
        super.layoutViewSubviews()
        
        const padding = this.core.paddingLength
        const bounds = this.view.contentBoundsWithInset(padding)
        
        bounds
            .rectangleByAddingY((RHButtons.compactHeight + RHSpacing.small))
            .rectangleWithHeight(this.tabBar)
            .assignedAsFrameOfView(this.tabBar)
            
            .rectangleForNextRow(padding, this.contentPanelView)
            .assignedAsFrameOfView(this.contentPanelView)
    }
    
}
